import React, { useMemo } from 'react';
import {
  Box,
  Drawer,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';

/** Matches layout Header / Sidebar dense toolbar height. */
const HEADER_HEIGHT = 56;

/**
 * Side drawer showing unmapped Excel columns stored as extraJson on a cost transaction.
 * Offset below the app AppBar (which uses zIndex drawer+1 and would otherwise clip the title).
 */
const BenchmarkExtraDataDrawer = ({ open, onClose, row }) => {
  const extras = useMemo(() => {
    if (!row?.extraJson) return null;
    try {
      const parsed = typeof row.extraJson === 'string' ? JSON.parse(row.extraJson) : row.extraJson;
      if (!parsed || typeof parsed !== 'object') return null;
      return parsed;
    } catch {
      return { raw: row.extraJson };
    }
  }, [row]);

  const entries = extras ? Object.entries(extras) : [];
  const lineLabel = row?.budgetCode || row?.boqItemNumber || row?.description || 'Line item';

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ zIndex: (theme) => theme.zIndex.drawer + 2 }}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 420 },
          top: HEADER_HEIGHT,
          height: `calc(100% - ${HEADER_HEIGHT}px)`,
          borderRadius: 0,
          boxShadow: 8,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}
      ModalProps={{
        BackdropProps: {
          sx: { top: HEADER_HEIGHT },
        },
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={600} noWrap>
            Other columns
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap title={lineLabel}>
            {lineLabel}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="Close">
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {entries.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No other Excel columns for this row.
          </Typography>
        ) : (
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>Column</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map(([key, value]) => (
                <TableRow key={key} hover>
                  <TableCell sx={{ verticalAlign: 'top', width: '40%', color: 'text.secondary' }}>
                    {key}
                  </TableCell>
                  <TableCell sx={{ wordBreak: 'break-word' }}>
                    {value == null ? '' : String(value)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>
    </Drawer>
  );
};

export default BenchmarkExtraDataDrawer;
